import type { Maybe } from '@adornis/base/utilTypes.js';
import { Category } from '@adornis/buchungsheld-db/entities/category.js';
import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import '@adornis/buchungsheld-theme/components/tr-button-small.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { XDropdownSelection } from '@adornis/popover/x-dropdown-selection.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { filter, switchMap, tap } from 'rxjs';
import { BHPromptCategoryModify } from '../prompts/bh-prompt-category-modify.js';

@customElement('bh-category-picker')
export class BHCategoryPicker extends XDropdownSelection<Category> {
  @property({ attribute: 'institution-id' }) institutionID = new RXController<string | null>(this, null);

  @state() private readonly categories = new RXController(
    this,
    this.institutionID.observable.pipe(
      filter(Boolean),
      switchMap(institutionID => Category.subscribeCategoriesByInstitution(institutionID)(Category.allFields)),
      tap(categories => {
        this.selectables.next(categories);
      }),
    ),
  );

  @state() private readonly _translation = new TranslationController<BHTranslationDictionary>(this);

  constructor() {
    super();

    this.placeholder = `${this._translation.translate('field', 'category')}*`;
    this.setAttribute('placeholder-mode', 'floating');
  }

  override render() {
    return html`
      <x-flex horizontal crossaxis-end space="sm" ${acss({ '&>*:nth-child(1)': { flex: '1' } })}>
        ${super.render()}
        <tr-button-small
          icon="add"
          .action=${async () => {
            const category = await BHPromptCategoryModify.add(this.institutionID.value ?? '');
            this.value.next(category);
            this.dispatchEvent(new CustomEvent('value-picked', { detail: { value: category } }));
          }}
        ></tr-button-small>
      </x-flex>
    `;
  }

  override renderString = (category: Maybe<Category>) => category?.name ?? 'keine Kategorie hier?';

  override renderItem = (category: Category) =>
    html`
      <x-flex padding="sm" horizontal crossaxis-center w100>
        <x-text flex> ${category.name} </x-text>
        <x-button
          mode="icon"
          icon="edit"
          @click=${async (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            category.name = await XDialog.prompt(this._translation.translate('PROFILE', 'NEW_NAME'), {
              placeholder: category.name,
            });
            await XDialog.guard(() => category.save());
          }}
        >
        </x-button>
        <x-button
          mode="icon"
          icon="delete"
          tone="error"
          @click=${async (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            await XDialog.guard(() => category.remove(), { hasToConfirm: true });
          }}
        ></x-button>
      </x-flex>
    `;
}
